import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Stack, TextField, MenuItem,
  Alert, Chip, CircularProgress, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, Snackbar,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import CancelIcon from '@mui/icons-material/Cancel';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  getDemoBookings, resendDemoBookingConfirmation, cancelDemoBookingAdmin,
  reassignDemoBooking, getUsers,
} from '../services/api';

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'Confirmed', label: 'Confirmed' },
  { value: 'Rescheduled', label: 'Rescheduled' },
  { value: 'Cancelled', label: 'Cancelled' },
  { value: 'Completed', label: 'Completed' },
  { value: 'NoShow', label: 'No-show' },
];

function statusChip(status) {
  const map = {
    Confirmed: { color: 'success' },
    Rescheduled: { color: 'info' },
    Cancelled: { color: 'default', variant: 'outlined' },
    Completed: { color: 'primary' },
    NoShow: { color: 'warning' },
  };
  const cfg = map[status] || { color: 'default' };
  return <Chip label={status} size="small" color={cfg.color} variant={cfg.variant || 'filled'} />;
}

function fmt(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

export default function DemoBookingsPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);

  const [statusFilter, setStatusFilter] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rowBusy, setRowBusy] = useState({});

  const [cancelDialog, setCancelDialog] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [reassignDialog, setReassignDialog] = useState(null);
  const [reassignUserId, setReassignUserId] = useState('');

  const setBusy = (id, flag) => setRowBusy((s) => ({ ...s, [id]: flag }));

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (from) params.from = new Date(from).toISOString();
      if (to) params.to = new Date(to).toISOString();
      const { data } = await getDemoBookings(params);
      setBookings(data || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, from, to]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getUsers().then(({ data }) => setUsers(data || [])).catch(() => {});
  }, []);

  const handleResend = async (row) => {
    setBusy(row.id, true);
    try {
      await resendDemoBookingConfirmation(row.id);
      setToast({ severity: 'success', message: `Confirmation re-sent to ${row.email}.` });
      load();
    } catch (e) {
      setToast({ severity: 'error', message: e.response?.data?.message || 'Resend failed' });
    } finally {
      setBusy(row.id, false);
    }
  };

  const handleCancel = async () => {
    if (!cancelDialog) return;
    setBusy(cancelDialog.id, true);
    try {
      await cancelDemoBookingAdmin(cancelDialog.id, cancelReason || null);
      setToast({ severity: 'success', message: 'Booking cancelled.' });
      setCancelDialog(null);
      setCancelReason('');
      load();
    } catch (e) {
      setToast({ severity: 'error', message: e.response?.data?.message || 'Cancel failed' });
    } finally {
      setBusy(cancelDialog.id, false);
    }
  };

  const handleReassign = async () => {
    if (!reassignDialog || !reassignUserId) return;
    setBusy(reassignDialog.id, true);
    try {
      await reassignDemoBooking(reassignDialog.id, Number(reassignUserId));
      setToast({ severity: 'success', message: 'Booking reassigned.' });
      setReassignDialog(null);
      setReassignUserId('');
      load();
    } catch (e) {
      setToast({ severity: 'error', message: e.response?.data?.message || 'Reassign failed' });
    } finally {
      setBusy(reassignDialog.id, false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>Demo &amp; Exchange Bookings</Typography>
        <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            select label="Status" size="small"
            value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            {STATUS_FILTERS.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
          </TextField>
          <TextField label="From" size="small" type="date"
            slotProps={{ inputLabel: { shrink: true } }} value={from} onChange={(e) => setFrom(e.target.value)} />
          <TextField label="To" size="small" type="date"
            slotProps={{ inputLabel: { shrink: true } }} value={to} onChange={(e) => setTo(e.target.value)} />
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
      ) : bookings.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No bookings match the current filters.</Typography>
        </Paper>
      ) : (
        <Stack spacing={1}>
          {bookings.map((b) => (
            <Paper key={b.id} sx={{ p: 2 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: { md: 'center' } }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {b.firstName} {b.lastName}
                    </Typography>
                    {statusChip(b.status)}
                    {b.rescheduleCount > 0 && (
                      <Chip label={`Rescheduled ×${b.rescheduleCount}`} size="small" variant="outlined" />
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {b.email}{b.company ? ` · ${b.company}` : ''}{b.jobTitle ? ` · ${b.jobTitle}` : ''}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <strong>{fmt(b.startTimeUtc)}</strong> – {fmt(b.endTimeUtc)} (UTC)
                    {b.timezone ? ` · prospect tz: ${b.timezone}` : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Host: {b.assignedUserName || `User #${b.assignedUserId}`}
                    {b.leadId && ` · Lead #${b.leadId}`}
                    {b.meetingId && ` · Meeting #${b.meetingId}`}
                    {b.joinPassword && ` · Password: ${b.joinPassword}`}
                  </Typography>
                </Box>

                <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.75 }}>
                  {b.joinUrl && (
                    <Tooltip title={b.joinPassword ? `Password: ${b.joinPassword}` : `Join ${b.joinProvider || 'meeting'}`}>
                      <Button size="small" variant="contained" color="success" startIcon={<OpenInNewIcon />}
                        component="a" href={b.joinUrl} target="_blank" rel="noopener noreferrer">
                        Join {b.joinProvider ? `(${b.joinProvider})` : 'Meeting'}
                      </Button>
                    </Tooltip>
                  )}
                  {b.leadId && (
                    <Tooltip title="Open lead">
                      <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />}
                        onClick={() => navigate(`/leads/${b.leadId}`)}>Lead</Button>
                    </Tooltip>
                  )}
                  <Button
                    size="small" variant="outlined" startIcon={<SendIcon />}
                    disabled={!!rowBusy[b.id] || b.status === 'Cancelled'}
                    onClick={() => handleResend(b)}
                  >
                    Resend
                  </Button>
                  <Button
                    size="small" variant="outlined" startIcon={<SwapHorizIcon />}
                    disabled={!!rowBusy[b.id] || b.status === 'Cancelled'}
                    onClick={() => { setReassignDialog(b); setReassignUserId(String(b.assignedUserId)); }}
                  >
                    Reassign
                  </Button>
                  <Button
                    size="small" variant="outlined" color="error" startIcon={<CancelIcon />}
                    disabled={!!rowBusy[b.id] || b.status === 'Cancelled'}
                    onClick={() => { setCancelDialog(b); setCancelReason(''); }}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Stack>
              {b.notes && (
                <Box sx={{ mt: 1.5, p: 1.5, background: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">Notes:</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{b.notes}</Typography>
                </Box>
              )}
            </Paper>
          ))}
        </Stack>
      )}

      {/* Cancel dialog */}
      <Dialog open={!!cancelDialog} onClose={() => setCancelDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel booking</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Cancel the booking for <strong>{cancelDialog?.firstName} {cancelDialog?.lastName}</strong>?
            Prospect will receive a cancellation email and the calendar invite will be retracted.
          </Typography>
          <TextField
            label="Reason (optional)" multiline rows={2} fullWidth
            value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog(null)}>Keep</Button>
          <Button color="error" variant="contained" onClick={handleCancel}>Cancel booking</Button>
        </DialogActions>
      </Dialog>

      {/* Reassign dialog */}
      <Dialog open={!!reassignDialog} onClose={() => setReassignDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Reassign host</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Pick a new host for {reassignDialog?.firstName} {reassignDialog?.lastName}'s booking.
          </Typography>
          <TextField
            select label="Host" fullWidth
            value={reassignUserId}
            onChange={(e) => setReassignUserId(e.target.value)}
          >
            {users.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.fullName || u.username} ({u.email})
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReassignDialog(null)}>Close</Button>
          <Button variant="contained" onClick={handleReassign} disabled={!reassignUserId}>Reassign</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}
